import React, { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Slider,
  Switch,
  TextField,
  Checkbox,
  FormHelperText,
  Snackbar,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import RosterParticipant from 'backend/models/roster_participant/roster_participant';
import { getCampYearsOptions } from 'backend/utils/campYears';
import {
  BM_TIMEZONE,
  formatBurnDateLabel,
  getBurnDates,
  getDateTimePickerBounds,
} from 'backend/utils/burnDates';
import { CurrentUserSignupStatus } from '../state/store';
import { ActiveRosterIDState, CurrentRosterState } from '../state/roster';
import { getFrontendConfig } from '../config/config';
import BackendRosterClient from '../api/roster/roster';
import BackendSettingsClient from '../api/settings/client';

const frontendConfig = getFrontendConfig();
const rosterClient = new BackendRosterClient(frontendConfig.BackendURL);
const settingsClient = new BackendSettingsClient(frontendConfig.BackendURL);
const DefaultEssentialMindSharkURL = 'https://rb.gy/zmxncc';

const toDT = (d: Date | null): DateTime | null =>
  d ? DateTime.fromJSDate(d).setZone(BM_TIMEZONE) : null;

const fromDT = (dt: DateTime | null): Date | null =>
  dt && dt.isValid ? dt.toJSDate() : null;

interface Props {
  handleSuccess: () => void;
  rosterParticipant: RosterParticipant;
}

interface RosterParticipantFormData {
  probabilityOfAttending: number;
  hasTicket: boolean;
  hasVehiclePass: boolean;
  extraTickets: boolean;
  yearsAttended: number;
  yearsAtCamp: number[];
  estimatedArrivalDate: Date | null;
  estimatedDepartureDate: Date | null;
  sleepingArrangement: string;
  earlyArrivalInterest: boolean;
  postBurnInterest: boolean;
  hasReadEssentialMindshark: boolean;
  agreesToParticipateInTearDown: boolean;
  agreesToParticipateInShifts: boolean;
  agreesToPayDues: boolean;
}

export function attendanceRemovalMessage(count: number): string {
  return count === 1
    ? 'Your attendance update removed 1 shift assignment that is now outside your attendance window.'
    : `Your attendance update removed ${count} shift assignments that are now outside your attendance window.`;
}

function RosterSignupFormV2({ handleSuccess, rosterParticipant }: Props) {
  const [formData, setFormData] = useState<RosterParticipantFormData>({
    probabilityOfAttending: rosterParticipant.probabilityOfAttending || 0,
    hasTicket: rosterParticipant.hasTicket || false,
    hasVehiclePass: rosterParticipant.hasVehiclePass || false,
    extraTickets: rosterParticipant.extraTickets || false,
    yearsAttended: rosterParticipant.yearsAttended || 0,
    yearsAtCamp: rosterParticipant.yearsAtCamp || [],
    estimatedArrivalDate: rosterParticipant.estimatedArrivalDate
      ? new Date(rosterParticipant.estimatedArrivalDate)
      : null,
    estimatedDepartureDate: rosterParticipant.estimatedDepartureDate
      ? new Date(rosterParticipant.estimatedDepartureDate)
      : null,
    sleepingArrangement: rosterParticipant.sleepingArrangement || '',
    earlyArrivalInterest: rosterParticipant.earlyArrivalInterest || false,
    postBurnInterest: rosterParticipant.postBurnInterest || false,
    hasReadEssentialMindshark:
      rosterParticipant.hasReadEssentialMindshark || false,
    agreesToParticipateInTearDown:
      rosterParticipant.agreesToParticipateInTearDown || false,
    agreesToParticipateInShifts:
      rosterParticipant.agreesToParticipateInShifts || false,
    agreesToPayDues: rosterParticipant.agreesToPayDues || false,
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [removedAssignmentCount, setRemovedAssignmentCount] = useState(0);
  const [essentialMindSharkURL, setEssentialMindSharkURL] = useState(
    DefaultEssentialMindSharkURL,
  );
  const userSignupStatus = useRecoilValue(CurrentUserSignupStatus);
  const activeRosterID = useRecoilValue(ActiveRosterIDState);
  const currentRoster = useRecoilValue(CurrentRosterState);

  const yearsAtCampOptions = useMemo(
    () => getCampYearsOptions(currentRoster.year),
    [currentRoster.year],
  );

  const burnInfo = useMemo(() => {
    const dates = getBurnDates(currentRoster.year);
    const bounds = getDateTimePickerBounds(currentRoster.year);
    return {
      bounds: {
        arrivalMin: DateTime.fromJSDate(bounds.arrivalMin).setZone(BM_TIMEZONE),
        arrivalMax: DateTime.fromJSDate(bounds.arrivalMax).setZone(BM_TIMEZONE),
        departureMaxEnd: DateTime.fromJSDate(bounds.departureMaxEnd).setZone(
          BM_TIMEZONE,
        ),
      },
      gatesLabel: formatBurnDateLabel(dates.gatesOpen),
      eaLabel: formatBurnDateLabel(dates.earlyArrivalStart),
      templeLabel: formatBurnDateLabel(dates.templeBurn),
    };
  }, [currentRoster.year]);

  useEffect(() => {
    settingsClient
      .GetFrontendLinks()
      .then((links) => setEssentialMindSharkURL(links.essentialMindSharkURL))
      .catch((error) =>
        console.error('Failed to load frontend link settings:', error),
      );
  }, []);

  const completeSubmission = () => {
    if (
      !userSignupStatus.hasCompletedPrivateProfile ||
      !userSignupStatus.hasCompletedPublicProfile
    ) {
      window.location.href = '/profile-edit';
    } else {
      handleSuccess();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await rosterClient.Signup(
        activeRosterID,
        formData as unknown as RosterParticipant,
      );
      setSnackbarOpen(true);
      if (result.removedAssignmentCount > 0) {
        setRemovedAssignmentCount(result.removedAssignmentCount);
        return;
      }
      completeSubmission();
    } catch (error) {
      console.error('Failed to submit form:', error);
    }
  };

  const handleChange = <K extends keyof RosterParticipantFormData>(
    field: K,
    value: RosterParticipantFormData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCloseSnackbar = (
    event: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
        {removedAssignmentCount > 0 && (
          <Alert
            action={
              <Button color="inherit" onClick={completeSubmission}>
                Continue
              </Button>
            }
            severity="warning"
            sx={{ mb: 2 }}
          >
            {attendanceRemovalMessage(removedAssignmentCount)} Review your
            remaining shifts after continuing.
          </Alert>
        )}
        <FormHelperText sx={{ mb: 2, color: 'text.secondary' }}>
          * Required fields
        </FormHelperText>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <FormLabel>Probability of attending (0-100) *</FormLabel>
              <Slider
                value={formData.probabilityOfAttending || 0}
                onChange={(_, value) =>
                  handleChange(
                    'probabilityOfAttending',
                    Array.isArray(value) ? value[0] : value,
                  )
                }
                min={0}
                max={100}
                marks
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.hasTicket || false}
                  onChange={(e) => handleChange('hasTicket', e.target.checked)}
                />
              }
              label="Do you have a ticket?"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.hasVehiclePass || false}
                  onChange={(e) =>
                    handleChange('hasVehiclePass', e.target.checked)
                  }
                />
              }
              label="Do you have a vehicle pass?"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.extraTickets || false}
                  onChange={(e) =>
                    handleChange('extraTickets', e.target.checked)
                  }
                />
              }
              label="Do you have extra tickets?"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              type="number"
              label="How many years have you attended the burn? *"
              required
              value={formData.yearsAttended ?? ''}
              onChange={(e) =>
                handleChange(
                  'yearsAttended',
                  e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0,
                )
              }
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel>
                How many years have you camped with MindShark?
              </FormLabel>
              <FormGroup>
                {yearsAtCampOptions.map((year) => (
                  <FormControlLabel
                    key={year}
                    control={
                      <Checkbox
                        checked={formData.yearsAtCamp?.includes(year) || false}
                        onChange={(e) => {
                          const newYears = e.target.checked
                            ? [...(formData.yearsAtCamp || []), year]
                            : (formData.yearsAtCamp || []).filter(
                                (y) => y !== year,
                              );
                          handleChange('yearsAtCamp', newYears);
                        }}
                      />
                    }
                    label={year.toString()}
                  />
                ))}
              </FormGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <DateTimePicker
              label="When do you plan to arrive? *"
              value={toDT(formData.estimatedArrivalDate)}
              onChange={(dt: DateTime | null) =>
                handleChange('estimatedArrivalDate', fromDT(dt))
              }
              timezone={BM_TIMEZONE}
              minDateTime={burnInfo.bounds.arrivalMin}
              maxDateTime={burnInfo.bounds.arrivalMax}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  helperText: (
                    <>
                      Gates open {burnInfo.gatesLabel}
                      <br />
                      EA begins {burnInfo.eaLabel}
                    </>
                  ),
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DateTimePicker
              label="When do you plan to depart? *"
              value={toDT(formData.estimatedDepartureDate)}
              onChange={(dt: DateTime | null) =>
                handleChange('estimatedDepartureDate', fromDT(dt))
              }
              timezone={BM_TIMEZONE}
              minDateTime={
                toDT(formData.estimatedArrivalDate) ??
                burnInfo.bounds.arrivalMin
              }
              maxDateTime={burnInfo.bounds.departureMaxEnd}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  helperText: `Temple burns ${burnInfo.templeLabel}`,
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth required>
              <FormLabel>What is your sleeping arrangement? *</FormLabel>
              <TextField
                select
                value={formData.sleepingArrangement || ''}
                onChange={(e) =>
                  handleChange('sleepingArrangement', e.target.value)
                }
                SelectProps={{
                  native: true,
                }}
              >
                <option value="">Select an option</option>
                <option value="Personal tent - something that requires camp shade">
                  Personal tent - something that requires camp shade
                </option>
                <option value="Personal yurt, shift pod, etc - something that doesn't require shade">
                  Personal yurt, shift pod, etc - something that doesn&apos;t
                  require shade
                </option>
                <option value="Other...">Other...</option>
              </TextField>
              <FormHelperText>
                *RVs are not permitted in our camp:* We *do not* have the space
                per unit person to have RVs in our camp, so that is not an
                acceptable answer. Modified vehicles (e.g., a school bus from
                Pimp My Ride) are OK.
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.earlyArrivalInterest || false}
                  onChange={(e) =>
                    handleChange('earlyArrivalInterest', e.target.checked)
                  }
                />
              }
              label="Are you interested in early arrival?"
            />
            <FormHelperText>
              This is a team of ~30 who arrive Wednesday or Thursday before the
              gates open to set up the camp and requires a special pass.
            </FormHelperText>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.postBurnInterest || false}
                  onChange={(e) =>
                    handleChange('postBurnInterest', e.target.checked)
                  }
                />
              }
              label="Are you interested in post-burn?"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.hasReadEssentialMindshark || false}
                  onChange={(e) =>
                    handleChange('hasReadEssentialMindshark', e.target.checked)
                  }
                />
              }
              label={
                <>
                  I have read the essential MindShark. (
                  <a
                    href={essentialMindSharkURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1976d2', textDecoration: 'underline' }}
                  >
                    {essentialMindSharkURL}
                  </a>
                  ) *
                </>
              }
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.agreesToParticipateInTearDown || false}
                  onChange={(e) =>
                    handleChange(
                      'agreesToParticipateInTearDown',
                      e.target.checked,
                    )
                  }
                />
              }
              label="I agree to participate in the camp tear-down. *"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.agreesToParticipateInShifts || false}
                  onChange={(e) =>
                    handleChange(
                      'agreesToParticipateInShifts',
                      e.target.checked,
                    )
                  }
                />
              }
              label="I agree to participate in camp shifts. *"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.agreesToPayDues || false}
                  onChange={(e) =>
                    handleChange('agreesToPayDues', e.target.checked)
                  }
                />
              }
              label="I agree to pay camp dues. *"
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={
                removedAssignmentCount > 0 ||
                !formData.probabilityOfAttending ||
                !formData.estimatedArrivalDate ||
                !formData.estimatedDepartureDate ||
                !formData.sleepingArrangement ||
                !formData.hasReadEssentialMindshark ||
                !formData.agreesToParticipateInTearDown ||
                !formData.agreesToParticipateInShifts ||
                !formData.agreesToPayDues
              }
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message="Roster Participant Updated!"
      />
    </>
  );
}

export default RosterSignupFormV2;
